import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [location] = useLocation();
  const isVerkaufenPage = location === '/verkaufen';

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };


  // JavaScript fallback for browsers without CSS scroll-timeline support (mobile Safari, older browsers)
  useEffect(() => {
    // Skip animation on /verkaufen page
    if (isVerkaufenPage) return;

    // Check if CSS scroll-timeline is supported
    if (CSS.supports && CSS.supports('animation-timeline', 'scroll()')) {
      console.log('✅ CSS scroll-timeline supported - using CSS animation');
      return; // Use CSS animation
    }

    console.log('📱 CSS scroll-timeline not supported - using JavaScript fallback');

    const handleScroll = () => {
      if (!headerRef.current) return;

      const scrollY = window.scrollY;
      const isMobile = window.innerWidth <= 767;

      // Define scroll ranges based on screen size (same as CSS)
      const startScroll = isMobile ? 100 : 150;
      const endScroll = isMobile ? 500 : 800;
      const finalWidth = isMobile ? `${Math.min(380, window.innerWidth - 32)}px` : '1000px';
      const finalPadding = isMobile ? '6px 12px' : '8px 16px';

      if (scrollY < startScroll) {
        // Transparent start state
        headerRef.current.style.width = 'calc(100% - 32px)';
        headerRef.current.style.borderRadius = '0';
        headerRef.current.style.background = 'transparent';
        headerRef.current.style.backdropFilter = 'none';
        headerRef.current.style.boxShadow = 'none';
        headerRef.current.style.padding = '12px 24px';
      } else if (scrollY >= endScroll) {
        // Full pill state
        headerRef.current.style.width = finalWidth;
        headerRef.current.style.borderRadius = '100vw';
        headerRef.current.style.background = 'rgba(255, 255, 255, 0.85)';
        headerRef.current.style.backdropFilter = 'blur(25px) saturate(200%)';
        headerRef.current.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(98, 213, 250, 0.15)';
        headerRef.current.style.padding = finalPadding;
      } else {
        // Animate between states with smooth interpolation
        const progress = (scrollY - startScroll) / (endScroll - startScroll);
        const easeProgress = progress * progress * (3 - 2 * progress); // Smooth ease curve

        // Interpolate width
        const startWidthPx = window.innerWidth - 32;
        const endWidthPx = isMobile ? Math.min(380, window.innerWidth - 32) : 1000;
        const currentWidth = startWidthPx + (endWidthPx - startWidthPx) * easeProgress;

        // Interpolate padding
        const startPadding = { x: 24, y: 12 };
        const endPadding = isMobile ? { x: 12, y: 6 } : { x: 16, y: 8 };
        const currentPadding = {
          x: startPadding.x + (endPadding.x - startPadding.x) * easeProgress,
          y: startPadding.y + (endPadding.y - startPadding.y) * easeProgress
        };

        // Apply interpolated styles
        headerRef.current.style.width = `${currentWidth}px`;
        headerRef.current.style.borderRadius = `${easeProgress * 100}vw`;
        headerRef.current.style.background = `rgba(255, 255, 255, ${0.85 * easeProgress})`;
        headerRef.current.style.backdropFilter = `blur(${25 * easeProgress}px) saturate(${100 + 100 * easeProgress}%)`;
        headerRef.current.style.boxShadow = `0 4px 16px rgba(0, 0, 0, ${0.12 * easeProgress}), 0 8px 32px rgba(98, 213, 250, ${0.15 * easeProgress})`;
        headerRef.current.style.padding = `${currentPadding.y}px ${currentPadding.x}px`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVerkaufenPage]);

  return (
    <header className="site-header">
      {/* Navigation - pillar container */}
      <div ref={headerRef} className={`inside-header ${isVerkaufenPage ? 'no-animation' : ''}`}>
        <nav className="flex items-center justify-between w-full">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <img
              src="/assets/YadeTransparent.png"
              alt="YADE Logo"
              className="h-8 sm:h-9 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-1 md:space-x-2 flex-1 justify-center">
            <Link href="/ueber-uns" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              Über uns
            </Link>
            <Link href="/kaufen" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              Kaufen
            </Link>
            {/* <Link href="/verkaufen" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              Verkaufen
            </Link> */}
            <Link href="/yade-signature" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              YADE Signature
            </Link>
            <Link href="/zukuenftige-projekte" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              Zukünftige Projekte
            </Link>
            <Link href="/vergangene-projekte" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              Vergangene Projekte
            </Link>
            <Link href="/blog" className="font-montserrat text-[15px] font-normal text-black hover:text-[#62D5FA] transition-colors px-2 py-1">
              Blog
            </Link>
          </div>

          {/* Right side - Termin Buchen button */}
          <div className="flex items-center ml-auto">
            <Link href="/kontakt" className="hidden md:flex items-center gap-2 bg-black text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-gray-800 transition-all duration-200">
              {/* Placeholder for Deniz photo - will be added when uploaded */}
              <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-xs text-white">
                D
              </div>
              <span>Termin Buchen</span>
              <span className="ml-1">→</span>
            </Link>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-black focus:outline-none"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile navigation menu */}
      <div className={`fixed inset-0 bg-white z-60 transform ${mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'} transition-transform duration-500 ease-out md:hidden`}>
        <div className="flex flex-col h-[100dvh] rounded-b-3xl overflow-hidden">
          <div className="flex justify-between items-center p-[2vh] border-b h-[10vh]">
            <h2 className="text-[calc(1rem+0.5vh)] font-montserrat font-bold">Menü</h2>
            <button onClick={closeMobileMenu}>
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-hidden flex flex-col">
            <div className="flex-1 flex flex-col justify-evenly py-[2vh] px-[4vw] text-center">
              <Link href="/ueber-uns" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                Über uns
              </Link>
              <Link href="/kaufen" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                Kaufen
              </Link>
              {/* <Link href="/verkaufen" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                Verkaufen
              </Link> */}
              <Link href="/yade-signature" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                YADE Signature
              </Link>
              <Link href="/zukuenftige-projekte" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                Zukünftige Projekte
              </Link>
              <Link href="/vergangene-projekte" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                Vergangene Projekte
              </Link>
              <Link href="/blog" className="block py-[1vh] text-[calc(1rem+0.8vh)] font-medium font-montserrat hover:text-[#62D5FA] transition-colors" onClick={closeMobileMenu}>
                Blog
              </Link>

              <div className="h-[18vh] flex items-end justify-center pb-[3vh]">
                <Link href="/kontakt" className="flex items-center justify-center gap-3 bg-black text-white rounded-full px-[4vw] py-[1.5vh] text-[calc(1rem+0.5vh)] font-medium" onClick={closeMobileMenu}>
                  <div className="w-[calc(1.5rem+1vh)] h-[calc(1.5rem+1vh)] rounded-full bg-gray-400 flex items-center justify-center text-[calc(0.75rem+0.3vh)] text-white">
                    D
                  </div>
                  <span>Termin Buchen</span>
                  <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
